import { useAppSelector } from "@store/store-helper";
import { fetchingTeamsFlagsSelector } from "@store/teams/teams-selector";
import { useMediaQueryList } from "@hooks/use-media-query";
import { TeamsEmptyPage } from "@pages/members/teams/teams-empty-page";
import { TeamsMobileView } from "@pages/members/teams/teams-mobile-view";
import { BaseCompanyIdProps } from "@custom-types/base-types";
import { SdbTeam, TeamsSortBy } from "@custom-types/teams-types";
import { TeamBulkButtonComponents } from "@components/common/faro-table/faro-table-types";
import { ColumnPreferencesProvider } from "@context-providers/table/column-preferences-provider";
import { TEAMS_TABLE_COLUMNS } from "@components/table/teams/teams-table-constants";
import { TeamsTable } from "@components/table/teams/teams-table";

interface Props extends BaseCompanyIdProps {
  /** The sorted teams to display */
  sortedTeams: SdbTeam[];

  /** The bulk action buttons for the teams table */
  buttonComponents: TeamBulkButtonComponents;

  /** Current sort by value */
  sortBy: TeamsSortBy;

  /** Callback to update the sort by value */
  setSortBy: (sortBy: TeamsSortBy) => void;
}

/** Wrapper component for the TeamsTable that handles different views and empty states */
export function TeamsTableWrapper({
  companyId,
  sortedTeams,
  buttonComponents,
  sortBy,
  setSortBy,
}: Props): JSX.Element {
  const { status } = useAppSelector(fetchingTeamsFlagsSelector);
  const { isScreenSmOrSmaller } = useMediaQueryList();

  const shouldShowEmptyPage =
    (sortedTeams.length === 0 && status === "succeeded") ||
    status === "rejected";

  const isLoading = status === "pending";

  if (shouldShowEmptyPage) {
    return <TeamsEmptyPage status={status} />;
  }

  if (isScreenSmOrSmaller) {
    return (
      <TeamsMobileView
        teams={sortedTeams}
        isLoading={isLoading}
        sortBy={sortBy}
      />
    );
  }

  return (
    <ColumnPreferencesProvider
      tableKey="teams-table"
      columns={[...TEAMS_TABLE_COLUMNS]}
      options={{
        nonConfigurableColumns: ["options"],
        defaultVisibleColumns: [
          "name",
          "users",
          "creationDate",
          "workspaceRole",
        ],
      }}
    >
      <TeamsTable
        companyId={companyId}
        teams={sortedTeams}
        isLoading={isLoading}
        bulkActionButtons={buttonComponents}
        sortBy={sortBy}
        setSortBy={setSortBy}
      />
    </ColumnPreferencesProvider>
  );
}
